import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, FileText, FolderOpen, Globe, Library, Search, X } from 'lucide-react'
import i18n from '@/i18n'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import {
  knowledgeApi,
  KnowledgeCollectionItem,
} from '@/lib/api'
import { KnowledgeAskSourceScope, KnowledgeDocumentListItem } from '@digital-twins/shared-types'
import { isScopeChannelOn } from './threadHelpers'

export interface KnowledgeScopeSelection extends KnowledgeAskSourceScope {
  web?: boolean
  library?: boolean
  mode: 'all' | 'selected'
  documentTokens: Array<string>
  collectionTokens: Array<string>
}

interface KnowledgeScopePickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: KnowledgeScopeSelection
  onApply: (value: KnowledgeScopeSelection) => void
  documents?: Array<KnowledgeDocumentListItem> | null
  collections?: Array<KnowledgeCollectionItem> | null
}

interface ChannelRowProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  icon: typeof Globe
  title: string
  description: string
  testId: string
}

type PickerTab = 'documents' | 'collections'

function fileTypeLabel(filename: string | null | undefined): string {
  const fallback = i18n.t('threads.scope.fileTypeFallback', { ns: 'app' })
  if (!filename) return fallback
  const ext = filename.split('.').pop()?.toUpperCase()
  return ext || fallback
}

function matchesQuery(haystack: string, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return haystack.toLowerCase().includes(needle)
}

function ChannelRow(props: ChannelRowProps) {
  const { checked, onCheckedChange, icon: Icon, title, description, testId } = props

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
        checked
          ? 'border-border bg-muted/60'
          : 'border-transparent bg-muted/25 hover:bg-muted/40',
      )}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
        onClick={() => onCheckedChange(!checked)}
      >
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            checked
              ? 'bg-primary/10 text-foreground'
              : 'bg-background text-muted-foreground',
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <Text as="span" size="sm" className="block leading-5">
            {title}
          </Text>
          <Text as="span" size="xs" color="muted" className="block leading-4">
            {description}
          </Text>
        </span>
      </button>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={cn(!checked && 'border border-border bg-muted')}
        data-testid={testId}
      />
    </div>
  )
}

export function KnowledgeScopePicker(props: KnowledgeScopePickerProps) {
  const {
    open,
    onOpenChange,
    value,
    onApply,
    documents: documentsProp = null,
    collections: collectionsProp = null,
  } = props
  const { t } = useTranslation('app')

  const [tab, setTab] = useState<PickerTab>('documents')
  const [documents, setDocuments] = useState<Array<KnowledgeDocumentListItem>>(
    documentsProp || [],
  )
  const [collections, setCollections] = useState<Array<KnowledgeCollectionItem>>(
    collectionsProp || [],
  )
  const [loading, setLoading] = useState(false)
  const [web, setWeb] = useState(() => isScopeChannelOn(value.web))
  const [library, setLibrary] = useState(() => isScopeChannelOn(value.library))
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(
    () => new Set(value.documentTokens),
  )
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(
    () => new Set(value.collectionTokens),
  )
  const [filterOpen, setFilterOpen] = useState(
    () => value.documentTokens.length > 0 || value.collectionTokens.length > 0,
  )
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    setWeb(isScopeChannelOn(value.web))
    setLibrary(isScopeChannelOn(value.library))
    setSelectedDocs(new Set(value.documentTokens))
    setSelectedCollections(new Set(value.collectionTokens))
    setFilterOpen(
      value.documentTokens.length > 0 || value.collectionTokens.length > 0,
    )
    setQuery('')
  }, [open, value.web, value.library, value.documentTokens, value.collectionTokens])

  useEffect(() => {
    if (!open) return
    if (documentsProp && collectionsProp) {
      setDocuments(documentsProp)
      setCollections(collectionsProp)
      return
    }
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const [docs, cols] = await Promise.all([
          documentsProp
            ? Promise.resolve(documentsProp)
            : knowledgeApi.listDocuments(),
          collectionsProp
            ? Promise.resolve(collectionsProp)
            : knowledgeApi.listCollections(),
        ])
        if (!cancelled) {
          setDocuments(docs)
          setCollections(cols)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, documentsProp, collectionsProp])

  const untitledDocument = t('threads.scope.untitledDocument')
  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) =>
        matchesQuery(doc.originalFilename || untitledDocument, query),
      ),
    [documents, query, untitledDocument],
  )
  const filteredCollections = useMemo(
    () => collections.filter((collection) => matchesQuery(collection.name, query)),
    [collections, query],
  )

  const selectedCount = selectedDocs.size + selectedCollections.size
  const canApply = web || library

  const selectedLabel = useMemo(() => {
    if (!library || selectedCount === 0) return t('threads.scope.apply')
    return t('threads.scope.useItems', { count: selectedCount })
  }, [library, selectedCount, t])

  function toggleDoc(token: string) {
    setSelectedDocs((prev) => {
      const next = new Set(prev)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }

  function toggleCollection(token: string) {
    setSelectedCollections((prev) => {
      const next = new Set(prev)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }

  function handleApply() {
    if (!canApply) return
    if (!library) {
      onApply({
        web,
        library: false,
        mode: 'all',
        documentTokens: [],
        collectionTokens: [],
      })
      onOpenChange(false)
      return
    }
    const documentTokens = Array.from(selectedDocs)
    const collectionTokens = Array.from(selectedCollections)
    const hasItems = documentTokens.length > 0 || collectionTokens.length > 0
    onApply({
      web,
      library: true,
      mode: hasItems ? 'selected' : 'all',
      documentTokens: hasItems ? documentTokens : [],
      collectionTokens: hasItems ? collectionTokens : [],
    })
    onOpenChange(false)
  }

  const filterSummary =
    selectedCount === 0
      ? t('threads.scope.allItems')
      : t('threads.scope.selectedCount', { count: selectedCount })

  const libraryFilter = library ? (
    <div className="min-w-0 overflow-hidden border-t border-border">
      <button
        type="button"
        aria-expanded={filterOpen}
        aria-controls="knowledge-scope-filter-panel"
        data-testid="knowledge-scope-filter-toggle"
        className="flex w-full items-center gap-2 px-5 py-3 text-left transition-colors hover:bg-muted/40"
        onClick={() => setFilterOpen((prev) => !prev)}
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            filterOpen ? 'rotate-0' : '-rotate-90',
          )}
        />
        <Text as="span" size="sm" className="flex-1 leading-5">
          {t('threads.scope.libraryItemsLabel')}
        </Text>
        <Badge variant="tag">{filterSummary}</Badge>
      </button>

      <div
        id="knowledge-scope-filter-panel"
        hidden={!filterOpen}
        data-testid="knowledge-scope-filter-panel"
      >
        <div className="px-5">
          <Tabs
            value={tab}
            onValueChange={(next) => setTab(next as PickerTab)}
          >
            <TabsList
              className="h-8 w-full rounded-lg bg-muted p-0.5"
              data-testid="knowledge-scope-tabs"
            >
              <TabsTrigger
                value="documents"
                variant="segmented"
                className="h-7 flex-1"
                data-testid="knowledge-scope-tab-documents"
              >
                {t('threads.scope.tabDocuments')}
              </TabsTrigger>
              <TabsTrigger
                value="collections"
                variant="segmented"
                className="h-7 flex-1"
                data-testid="knowledge-scope-tab-collections"
              >
                {t('threads.scope.tabCollections')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="px-5 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('threads.scope.searchPlaceholder')}
              aria-label={t('threads.scope.searchAria')}
              className="h-8 rounded-lg bg-muted/40 pl-8 pr-8 text-sm"
              data-testid="knowledge-scope-search"
            />
            {query ? (
              <button
                type="button"
                aria-label={t('threads.scope.searchClear')}
                className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                data-testid="knowledge-scope-search-clear"
                onClick={() => setQuery('')}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>

        <div
          className="max-h-[min(420px,50vh)] min-w-0 overflow-x-hidden overflow-y-auto px-3 py-2"
          data-testid="knowledge-scope-list"
        >
          {loading ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.loading')}
            </Text>
          ) : tab === 'documents' ? (
            documents.length === 0 ? (
              <Text size="sm" color="muted" className="px-2 py-6 leading-5">
                {t('threads.scope.noDocuments')}
              </Text>
            ) : filteredDocuments.length === 0 ? (
              <Text size="sm" color="muted" className="px-2 py-6 leading-5">
                {t('threads.scope.noMatchingDocuments', { query: query.trim() })}
              </Text>
            ) : (
              filteredDocuments.map((doc) => {
                const checked = selectedDocs.has(doc.token)
                return (
                  <label
                    key={doc.token}
                    className={cn(
                      'flex min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-lg px-2 py-2 transition-colors',
                      checked ? 'bg-accent' : 'hover:bg-muted/50',
                    )}
                    data-testid={`knowledge-scope-doc-${doc.token}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleDoc(doc.token)}
                      data-testid={`knowledge-scope-doc-check-${doc.token}`}
                    />
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <span
                      className="min-w-0 max-w-full flex-1 truncate text-sm leading-5 text-foreground"
                      title={doc.originalFilename || t('threads.scope.untitledDocument')}
                    >
                      {doc.originalFilename || t('threads.scope.untitledDocument')}
                    </span>
                    <Badge variant="tag" className="shrink-0">
                      {fileTypeLabel(doc.originalFilename)}
                    </Badge>
                  </label>
                )
              })
            )
          ) : collections.length === 0 ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.noCollections')}
            </Text>
          ) : filteredCollections.length === 0 ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.noMatchingCollections', { query: query.trim() })}
            </Text>
          ) : (
            filteredCollections.map((collection) => {
              const checked = selectedCollections.has(collection.token)
              const count = collection.documentTokens?.length ?? 0
              return (
                <label
                  key={collection.token}
                  className={cn(
                    'flex min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-lg px-2 py-2 transition-colors',
                    checked ? 'bg-accent' : 'hover:bg-muted/50',
                  )}
                  data-testid={`knowledge-scope-collection-${collection.token}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleCollection(collection.token)}
                    data-testid={`knowledge-scope-collection-check-${collection.token}`}
                  />
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span
                    className="min-w-0 max-w-full flex-1 truncate text-sm leading-5 text-foreground"
                    title={collection.name}
                  >
                    {collection.name}
                  </span>
                  <Badge variant="tag" className="shrink-0">
                    {t('threads.scope.items', { count })}
                  </Badge>
                </label>
              )
            })
          )}
        </div>
      </div>
    </div>
  ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-w-0 max-w-md gap-0 overflow-hidden p-0 sm:rounded-2xl"
        data-testid="knowledge-scope-picker"
      >
        <DialogHeader className="border-b border-border px-5 py-4 text-left">
          <DialogTitle>{t('threads.scope.dialogTitle')}</DialogTitle>
          <DialogDescription className="mt-1.5 leading-5">
            {t('threads.scope.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 px-5 py-4">
          <ChannelRow
            checked={web}
            onCheckedChange={setWeb}
            icon={Globe}
            title={t('threads.scope.chipWeb')}
            description={t('threads.scope.webDescription')}
            testId="knowledge-scope-web"
          />
          <ChannelRow
            checked={library}
            onCheckedChange={setLibrary}
            icon={Library}
            title={t('threads.scope.chipLibrary')}
            description={t('threads.scope.libraryDescription')}
            testId="knowledge-scope-library"
          />
        </div>

        {libraryFilter}

        <DialogFooter className="border-t border-border px-5 py-3 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="knowledge-scope-cancel"
            onClick={() => onOpenChange(false)}
          >
            {t('threads.scope.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="knowledge-scope-apply"
            disabled={!canApply}
            aria-disabled={!canApply}
            onClick={handleApply}
          >
            {selectedLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
